import React, { useEffect, useState } from 'react';
import { apiGet, apiPut } from '../utils/api';

type SportItem = {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
};

const Sports: React.FC = () => {
  const [items, setItems] = useState<SportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiGet<SportItem[]>('/players/sports');
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      console.error('Ошибка загрузки Sport:', e);
      setError(e?.message || 'Не удалось загрузить справочник Sport');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateItem = (index: number, patch: Partial<SportItem>) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const addItem = () => {
    const nextOrder =
      items.length > 0 ? Math.max(...items.map((item) => Number(item.sortOrder || 0))) + 10 : 10;
    setItems((prev) => [
      ...prev,
      {
        id: `SPORT_${Date.now()}`,
        code: '',
        name: '',
        sortOrder: nextOrder,
        isActive: true
      }
    ]);
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      await apiPut('/players/sports', { items });
      await load();
    } catch (e: any) {
      console.error('Ошибка сохранения Sport:', e);
      setError(e?.message || 'Не удалось сохранить справочник Sport');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Справочник Sport</h1>
          <p className="text-sm text-gray-600">Список видов спорта для уровней и настроек.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={addItem}
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            disabled={loading || saving}
          >
            Добавить
          </button>
          <button
            type="button"
            onClick={save}
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400"
            disabled={loading || saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Sort order</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((item, index) => (
              <tr key={item.id || `${item.code}-${index}`}>
                <td className="px-4 py-2">
                  <input
                    value={item.code}
                    onChange={(e) => updateItem(index, { code: e.target.value.toUpperCase() })}
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    value={item.name}
                    onChange={(e) => updateItem(index, { name: e.target.value })}
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="number"
                    value={item.sortOrder}
                    onChange={(e) => updateItem(index, { sortOrder: Number(e.target.value) })}
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm text-right"
                  />
                </td>
                <td className="px-4 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={item.isActive}
                    onChange={(e) => updateItem(index, { isActive: e.target.checked })}
                    className="h-4 w-4"
                  />
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                  Справочник пуст.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Sports;
